import React, { useMemo, useCallback } from 'react';
import { Form, InputNumber, Switch, Row, Col, Divider, Button, Space, Input, Card, TimePicker } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

interface TimeSlot {
  value: string;
  label: string;
}

interface OrdersSettingsProps {
  onSlotsChange?: () => void;
}

export const OrdersSettings: React.FC<OrdersSettingsProps> = ({ onSlotsChange }) => {
  const form = Form.useFormInstance();

  // ✅ ИСПРАВЛЕНО: Читаем напрямую из формы без state
  const timeSlots = useMemo(() => {
    const jsonValue = form.getFieldValue('delivery_time_slots');
    
    if (!jsonValue) {
      return [];
    }
    
    try {
      const parsed = typeof jsonValue === 'string' 
        ? JSON.parse(jsonValue) 
        : jsonValue;
      
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Ошибка парсинга слотов:', e);
      return [];
    }
  }, [form, form.getFieldValue('delivery_time_slots')]);

  // ✅ ИСПРАВЛЕНО: Единая функция для обновления слотов
  const updateSlots = useCallback((newSlots: TimeSlot[]) => {
    const jsonString = JSON.stringify(newSlots, null, 2);
    form.setFieldsValue({
      delivery_time_slots: jsonString
    });
    
    if (onSlotsChange) {
      setTimeout(onSlotsChange, 50);
    }
  }, [form, onSlotsChange]);

  const addTimeSlot = () => {
    updateSlots([
      ...timeSlots,
      { value: '09:00-14:00', label: '09:00 - 14:00' }
    ]);
  };

  const removeTimeSlot = (index: number) => {
    updateSlots(timeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index: number, timeRange: [Dayjs | null, Dayjs | null] | null) => {
    if (!timeRange || !timeRange[0] || !timeRange[1]) return;

    const [start, end] = timeRange;
    const startStr = start.format('HH:mm');
    const endStr = end.format('HH:mm');
    
    const newSlots = [...timeSlots];
    newSlots[index] = {
      value: `${startStr}-${endStr}`,
      label: `${startStr} - ${endStr}`
    };
    
    updateSlots(newSlots);
  };

  const parseTimeRange = (slot: TimeSlot): [Dayjs, Dayjs] | undefined => {
    try {
      const [start, end] = slot.value.split('-');
      return [dayjs(start, 'HH:mm'), dayjs(end, 'HH:mm')];
    } catch {
      return undefined;
    }
  };

  return (
    <>
      <Divider orientation="left">Минимальная сумма заказа</Divider>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Минимальная сумма заказа (₸)"
            name="minimum_order_amount"
            rules={[{ required: true, message: 'Укажите сумму' }]}
          >
            <InputNumber<number>
              min={0}
              max={1000000}
              style={{ width: '100%' }}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={value => {
                const parsed = Number(value!.replace(/\s/g, ''));
                return isNaN(parsed) ? 0 : parsed;
              }}
              addonAfter="₸"
            />
          </Form.Item>
        </Col>
        
        <Col xs={24} md={12}>
          <Form.Item
            label="Включить проверку минимальной суммы"
            name="is_minimum_order_enabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Временные слоты доставки</Divider>
      
      <div style={{ marginBottom: 16 }}>
        {timeSlots.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              📦 Временные слоты не настроены
            </p>
            <p style={{ fontSize: '14px', color: '#bbb' }}>
              Нажмите кнопку ниже чтобы добавить первый слот доставки
            </p>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {timeSlots.map((slot, index) => (
              <Card 
                key={index} 
                size="small"
                extra={
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeTimeSlot(index)}
                  >
                    Удалить
                  </Button>
                }
              >
                <Space>
                  <span style={{ minWidth: 60 }}>Слот {index + 1}:</span>
                  <TimePicker.RangePicker
                    format="HH:mm"
                    value={parseTimeRange(slot)}
                    onChange={(times) => updateTimeSlot(index, times)}
                    placeholder={['Начало', 'Конец']}
                  />
                  <span style={{ color: '#666' }}>
                    ({slot.value})
                  </span>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <Button 
        type="dashed" 
        onClick={addTimeSlot}
        icon={<PlusOutlined />}
        block
      >
        Добавить временной слот
      </Button>

      {/* Скрытое поле для хранения JSON */}
      <Form.Item name="delivery_time_slots" hidden>
        <Input.TextArea />
      </Form.Item>
    </>
  );
};